import type { BillingPaymentViewModel } from '@/components/billing/billing-types';
import { BillingEmptyState } from '@/components/billing/billing-empty-state';
import { PaymentDetailDrawer } from '@/components/billing/payment-detail-drawer';
import { paymentStatusBadgeVariant } from '@/components/billing/payment-status';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  CenteredContent,
  CenteredContentLayout,
  Header,
} from '@/layouts/centered-content.layout';
import { SidebarLayout } from '@/layouts/sidebar.layout';
import { cn } from '@/lib/utils';
import { useMemo, useState, type KeyboardEvent } from 'react';

interface BillingPresenterProps {
  hasSubscription: boolean;
  planName: string;
  planPrice: string;
  nextPaymentDate: string;
  hasStoredPaymentMethod: boolean;
  payments: BillingPaymentViewModel[];
  isPayingPayment: boolean;
  onPayWithStoredCard: (paymentId: string) => void;
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="gap-0 bg-secondary px-3 py-2">
        <CardTitle className="text-xs font-medium text-secondary-foreground">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 py-2.5">
        <p className="text-sm font-semibold leading-tight">{value}</p>
      </CardContent>
    </Card>
  );
}

export function BillingPresenter({
  hasSubscription,
  planName,
  planPrice,
  nextPaymentDate,
  hasStoredPaymentMethod,
  payments,
  isPayingPayment,
  onPayWithStoredCard,
}: BillingPresenterProps) {
  const { LL } = useI18nContext();
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(
    null
  );

  const selectedPayment = useMemo(
    () => payments.find((payment) => payment.id === selectedPaymentId) ?? null,
    [payments, selectedPaymentId]
  );

  function openPaymentDrawer(paymentId: string) {
    setSelectedPaymentId(paymentId);
  }

  function handlePaymentRowKeyDown(
    event: KeyboardEvent<HTMLTableRowElement>,
    paymentId: string
  ) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openPaymentDrawer(paymentId);
    }
  }

  return (
    <SidebarLayout className="!p-0">
      <CenteredContentLayout>
        <Header>
          <h1 className="text-lg tracking-tight sm:text-xl">
            {LL.billingPage.title()}
          </h1>
        </Header>

        <CenteredContent className="flex flex-col gap-6 pb-8 sm:pb-12">
          {hasSubscription ? (
            <>
              <div className="grid gap-4 md:grid-cols-3">
                <SummaryCard label={LL.billingPage.plan()} value={planName} />
                <SummaryCard label={LL.billingPage.price()} value={planPrice} />
                <SummaryCard
                  label={LL.billingPage.nextPayment()}
                  value={nextPaymentDate}
                />
              </div>

              <div className="flex flex-col gap-4">
                <h2 className="text-lg font-semibold">
                  {LL.billingPage.invoices()}
                </h2>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{LL.billingPage.invoiceDate()}</TableHead>
                      <TableHead>{LL.billingPage.invoiceAmount()}</TableHead>
                      <TableHead>{LL.billingPage.invoiceStatus()}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          className="text-center text-muted-foreground"
                        >
                          {LL.billingPage.emptyInvoices()}
                        </TableCell>
                      </TableRow>
                    ) : (
                      payments.map((payment) => (
                        <TableRow
                          key={payment.id}
                          className="cursor-pointer"
                          tabIndex={0}
                          role="button"
                          onClick={() => openPaymentDrawer(payment.id)}
                          onKeyDown={(event) =>
                            handlePaymentRowKeyDown(event, payment.id)
                          }
                        >
                          <TableCell>{payment.date}</TableCell>
                          <TableCell>{payment.amount}</TableCell>
                          <TableCell>
                            <Badge
                              variant={paymentStatusBadgeVariant(
                                payment.status
                              )}
                              className={cn(
                                (payment.status === 'void' ||
                                  payment.status === 'other') &&
                                  'text-muted-foreground'
                              )}
                            >
                              {payment.statusLabel}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <BillingEmptyState />
          )}
        </CenteredContent>
      </CenteredContentLayout>

      {hasSubscription ? (
        <PaymentDetailDrawer
          open={selectedPayment !== null}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedPaymentId(null);
            }
          }}
          payment={selectedPayment}
          hasStoredPaymentMethod={hasStoredPaymentMethod}
          isPaying={isPayingPayment}
          onPayWithStoredCard={onPayWithStoredCard}
        />
      ) : null}
    </SidebarLayout>
  );
}
